/**
 * AdvisorTable Component (T130 - US6)
 * Displays paginated list of financial advisors with actions
 * 
 * IMPORTANT: Displays total count reflecting applied filters
 * as per user requirement: "all lists should display the totals count,
 * if they are filtered, should display the total number of the rows
 * from that applied filter"
 * 
 * Features:
 * - Sticky header that remains visible during scroll
 * - Sticky footer showing total count
 * - Internal scrolling (max-height with overflow)
 * - Displays name, firm, total clients, active clients, join date, actions
 */

import { format } from 'date-fns';
import { Edit2, Trash2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { AdvisorTableSkeleton } from './AdvisorTableSkeleton';
import type { AdminAdvisor } from '../types/admin-advisor.type';


type AdvisorTableProps = {
  advisors: AdminAdvisor[];
  totalCount: number;
  isFiltered?: boolean;
  onEdit: (advisorId: string) => void;
  onDelete: (advisorId: string) => void;
  isLoading?: boolean;
  maxHeight?: string;
};

/**
 * Advisor table component displaying advisor list with actions
 * 
 * @param advisors - Array of advisors to display
 * @param totalCount - Total number of advisors (reflects filters if applied)
 * @param isFiltered - Whether filters are applied
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 * @param isLoading - Whether data is loading
 * 
 * Features:
 * - Displays name, firm, total clients, active clients, join date, actions
 * - Shows total count (filtered or unfiltered)
 * - Edit and delete actions per row
 * - Formatted dates
 * - Empty state message
 */
export function AdvisorTable({
  advisors,
  totalCount,
  isFiltered = false,
  onEdit,
  onDelete,
  isLoading = false,
  maxHeight = 'calc(100vh - 250px)',
}: AdvisorTableProps) {
  // Show loading skeleton
  if (isLoading) {
    return <div data-testid="advisor-table-skeleton"><AdvisorTableSkeleton /></div>;
  }

  // Empty state
  if (advisors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-gray-400 mb-2" data-testid="empty-state-illustration">
          <svg
            className="h-12 w-12 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            data-testid="empty-state-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        {isFiltered ? (
          <>
            <p className="text-gray-500 font-medium">No advisors match your filters</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your search terms
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-500 font-medium">No advisors found</p>
            <p className="text-gray-400 text-sm mt-1">
              Advisors are created through user registration
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-gray-200 rounded-md bg-white">
      {/* Sticky Header - Total count display */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="text-sm">
          {isFiltered ? (
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900">
                {advisors.length.toLocaleString()}
              </span>
              {' '}of{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString()}
              </span>
              {' '}advisors{' '}
              <span className="text-[#026857] font-medium">(Filtered)</span>
            </span>
          ) : (
            <span>
              Total{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString()}
              </span>
              {' '}
              {totalCount === 1 ? 'advisor' : 'advisors'}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable table container */}
      <div 
        className="overflow-auto"
        style={{ maxHeight }}
      >
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Firm
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Firm Size
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Advisor Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Clients
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Active Clients
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Join Date
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {advisors.map((advisor) => {
              // Compute display name from user object
              const displayName = advisor.name || 
                `${advisor.user.firstName} ${advisor.user.lastName || ''}`.trim();
              
              return (
                <tr
                  key={advisor.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {/* Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">
                        {displayName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {advisor.user.email}
                      </div>
                    </div>
                  </td>

                  {/* Firm */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {advisor.companyName}
                    </div>
                  </td>

                  {/* Firm Size Segment (Feature 071) */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {advisor.firmSizeSegment ? (
                      <Badge variant="secondary">{advisor.firmSizeSegment}</Badge>
                    ) : (
                      <span className="text-xs text-gray-400">Not set</span>
                    )}
                  </td>

                  {/* Advisor Role (Feature 071) */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline">Financial Advisor</Badge>
                      {advisor.isFirmChampion ? (
                        <Badge variant="secondary">Firm Champion</Badge>
                      ) : null}
                    </div>
                  </td>

                  {/* Total Clients */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {advisor.clientsCount ?? 0}
                  </td>

                  {/* Active Clients */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {advisor.activeClients ?? advisor.clientsCount ?? 0}
                  </td>

                  {/* Join Date */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(advisor.createdAt), 'MMM d, yyyy')}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(advisor.id)}
                        className="p-1.5 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10 rounded-md transition-colors"
                        aria-label={`Edit ${displayName}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(advisor.id)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        aria-label={`Delete ${displayName}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sticky Footer - Summary info */}
      <div className="sticky bottom-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
        <div>
          {advisors.length > 0 && (
            <span>
              Displaying {advisors.length} {advisors.length === 1 ? 'row' : 'rows'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span>Scroll for more →</span>
        </div>
      </div>
    </div>
  );
}
